import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input, Select, Spinner } from '@components/ui';
import {
  CTA,
  GHOST,
  FORM_SKIN,
  INSET_PANEL,
  SECTION_NUM,
  DASHED_RULE_STYLE,
} from '@components/ui/designTokens';
import {
  IdentificationIcon,
  MapPinIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { AddressListManager } from './AddressListManager';
import { useCountries } from '../hooks/useGeolocation';
import { getDefaultCatalogId } from '@lib/utils/catalogDefaults';
import { customerService } from '../services/customerService';
import { TAX_ID_REGEX, TAX_ID_MIN_LENGTH, NO_HTML_NAME_REGEX } from '@/utils/businessValidation';

/** DGI "tipoRec" (texto) → nuestro código de receptor DGI (01-04). */
function mapTipoRecToReceiverType(tipoRec: string): string {
  const mapping: Record<string, string> = {
    'Contribuyente': '01',
    'Consumidor Final': '02',
    'Gobierno': '03',
    'Extranjero': '04',
  };
  return mapping[tipoRec] || '01';
}

import type { BusinessCustomer, Address, CreateBusinessCustomerInput } from '../types/customer.types';

/**
 * Formulario de cliente Comercial/Business (persona jurídica) — migrado del
 * legado `_FormBusinessCustomer.cshtml`. A diferencia del cliente Particular,
 * acá el DV SÍ es requerido (mismo criterio que el legado).
 */
const businessSchema = z.object({
  legalName: z.string().min(1, 'La razón social es requerida').max(100, 'Máximo 100 caracteres')
    .regex(NO_HTML_NAME_REGEX, 'No se permiten etiquetas HTML ni espacios al inicio/final'),
  comercialName: z.string().min(1, 'El nombre comercial es requerido').max(100, 'Máximo 100 caracteres')
    .regex(NO_HTML_NAME_REGEX, 'No se permiten etiquetas HTML ni espacios al inicio/final'),
  ruc: z.string().min(1, 'El RUC es requerido').max(50, 'Máximo 50 caracteres')
    .refine((val) => val.length >= TAX_ID_MIN_LENGTH && TAX_ID_REGEX.test(val), {
      message: `Formato de RUC inválido (mínimo ${TAX_ID_MIN_LENGTH} caracteres)`,
    }),
  dv: z.string().min(1, 'El DV es requerido').max(4, 'Máximo 4 caracteres')
    .regex(/^\d{2,4}$/, 'El DV debe ser numérico, entre 2 y 4 dígitos'),
  recep_type: z.string().min(1, 'El tipo de receptor DGI es requerido'),
  countryId: z.string().optional().or(z.literal('')),
  email: z.string().min(1, 'El email es requerido').email('Email inválido').max(100, 'Máximo 100 caracteres'),
  phone: z.string()
    .refine((val) => {
      if (!val || val.trim() === '') return true;
      if (val.length < 7 || val.length > 16) return false;
      if (!/^[\d\-]+$/.test(val)) return false;
      const digitsOnly = val.replace(/-/g, '');
      return digitsOnly.length >= 7 && digitsOnly.length <= 15;
    }, { message: 'Formato DGI: 999-9999, 9999-9999 o código-999-9999 (7-16 chars)' })
    .or(z.literal('')),
});

type BusinessFormData = z.infer<typeof businessSchema>;

interface CustomerFormBusinessProps {
  customer?: BusinessCustomer;
  onSubmit: (data: CreateBusinessCustomerInput) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const STEPS = [
  { id: 'identidad', num: '01', label: 'Identidad y contacto', icon: IdentificationIcon },
  { id: 'direcciones', num: '02', label: 'Direcciones', icon: MapPinIcon },
] as const;
const LAST_STEP = STEPS.length - 1;

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-3">
      <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-secondary-400">{children}</span>
      <span className="h-px flex-1" style={DASHED_RULE_STYLE} />
    </div>
  );
}

export function CustomerFormBusiness({ customer, onSubmit, onCancel, isLoading }: CustomerFormBusinessProps) {
  const [addresses, setAddresses] = useState<Address[]>(customer?.address || []);
  const [step, setStep] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 0 }); }, [step]);

  const { data: countries, isLoading: isLoadingCountries } = useCountries();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<BusinessFormData>({
    resolver: zodResolver(businessSchema),
    mode: 'onChange',
    defaultValues: {
      legalName: customer?.legalName || '',
      comercialName: customer?.comercialName || '',
      ruc: customer?.ruc || '',
      dv: customer?.dv || '',
      recep_type: customer?.recepType || '01',
      countryId: customer?.countryId || '',
      email: customer?.email || '',
      phone: customer?.phone || '',
    },
  });

  useEffect(() => {
    if (!customer && !watch('countryId') && countries?.length) {
      setValue('countryId', getDefaultCatalogId(countries) || '');
    }
  }, [countries, customer, setValue, watch]);

  const legalName = watch('legalName');
  const ruc = watch('ruc');
  const dv = watch('dv');
  const recepType = watch('recep_type');

  /** Verificación de RUC con la DGI — restaurada tal como estaba en el
   * formulario único anterior (`customerService.verifyRucDV`). */
  const handleVerifyRuc = async () => {
    if (!ruc || !dv || !recepType) {
      setVerificationMessage('Por favor completa RUC, DV y Tipo de Receptor DGI');
      return;
    }

    setIsVerifying(true);
    setVerificationMessage('');

    try {
      // La DGI (feConsRucDV) espera dTipoRuc = 1 (Natural) o 2 (Jurídico), NO el
      // Tipo de Receptor DGI (01-04). Un cliente Comercial siempre es Jurídico.
      const result = await customerService.verifyRucDV(ruc, dv, '2');

      if (result.success && result.data) {
        setValue('legalName', result.data.nombre);
        setValue('recep_type', mapTipoRecToReceiverType(result.data.tipoRec));

        setVerificationMessage(`✓ Verificado: ${result.data.nombre} (${result.data.tipoRec})`);
      } else {
        setVerificationMessage(`✗ ${result.message}`);
      }
    } catch (error) {
      setVerificationMessage('✗ Error al verificar RUC con la DGI');
      console.error('Error verifying RUC:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleFormSubmit = (data: BusinessFormData) => {
    const submitData: CreateBusinessCustomerInput = {
      customerKind: 'BUSINESS',
      legalName: data.legalName,
      comercialName: data.comercialName,
      ruc: data.ruc,
      dv: data.dv,
      countryId: data.countryId || undefined,
      recep_type: data.recep_type,
      email: data.email,
      phone: data.phone,
      address: addresses,
    };
    onSubmit(submitData);
  };

  // Al entrar al último paso se bloquea "Actualizar/Crear Cliente" ~600 ms: así
  // el segundo clic de un doble-clic sobre "Continuar" no cae sobre el botón de
  // guardar (ambos ocupan el mismo lugar del pie). El botón se ve deshabilitado,
  // no es un clic muerto. "Continuar" (goNext) nunca guarda.
  const [submitLocked, setSubmitLocked] = useState(false);
  const lockTimerRef = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(lockTimerRef.current), []);

  const armLastStep = () => {
    setSubmitLocked(true);
    window.clearTimeout(lockTimerRef.current);
    lockTimerRef.current = window.setTimeout(() => setSubmitLocked(false), 600);
  };

  const handleValidSubmit = (data: BusinessFormData) => {
    if (step !== LAST_STEP) { setStep(LAST_STEP); armLastStep(); return; }
    if (submitLocked) return;
    handleFormSubmit(data);
  };

  const goNext = async () => {
    const valid = await trigger();
    if (valid) { setStep(LAST_STEP); armLastStep(); }
  };

  return (
    <form onSubmit={handleSubmit(handleValidSubmit)} className="flex min-h-0 flex-1 flex-col px-7 py-6">
      <div className={`${INSET_PANEL} flex items-stretch gap-1 p-1.5`}>
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const isActive = step === i;
          const isDone = step > i;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => (i < step ? setStep(i) : i > step ? goNext() : undefined)}
              aria-current={isActive ? 'step' : undefined}
              className={`flex flex-1 items-center gap-2.5 rounded-lg px-3.5 py-2.5 text-left transition-colors ${
                isActive ? 'bg-ink-850 text-white' : 'text-secondary-500 hover:bg-white hover:text-ink-850 dark:hover:bg-secondary-800 dark:hover:text-white'
              }`}
            >
              <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-mono text-[11px] font-bold ${
                isActive ? 'bg-white/[.15] text-white' : isDone ? 'bg-teal-500/10 text-[#0d9488]' : 'bg-white text-secondary-400 dark:bg-secondary-800'
              }`}>
                {isDone ? <CheckIcon className="h-3 w-3" strokeWidth={3} /> : s.num}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-semibold">{s.label}</span>
              </span>
              <Icon className={`h-3.5 w-3.5 shrink-0 ${isActive ? 'text-white/70' : 'text-secondary-300'}`} />
            </button>
          );
        })}
      </div>

      <div className="mt-5 flex-1 min-h-0 overflow-y-auto pr-1">
        <div className={`${FORM_SKIN} ${step === 0 ? 'flex flex-col gap-5' : 'hidden'}`}>
          <div>
            <GroupLabel>Razón Social</GroupLabel>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input label="Razón Social" placeholder="Nombre legal de la empresa" error={errors.legalName?.message}
                maxLength={100} required {...register('legalName')} data-testid="customer-business-legal-name-input" />
              <Input label="Nombre Comercial" placeholder="Nombre comercial" error={errors.comercialName?.message}
                maxLength={100} required {...register('comercialName')} data-testid="customer-business-comercial-name-input" />
            </div>
          </div>

          <div>
            <GroupLabel>Documento</GroupLabel>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Input label="RUC" placeholder="0000000-0-000000" error={errors.ruc?.message} maxLength={50}
                  required {...register('ruc')} data-testid="customer-business-ruc-input" />
              </div>
              <div className="col-span-1">
                <Input label="DV" placeholder="00" error={errors.dv?.message} maxLength={4} required
                  {...register('dv')} data-testid="customer-business-dv-input" />
              </div>
            </div>

            <div className="mt-3.5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Select label="Tipo de Receptor DGI" error={errors.recep_type?.message} required
                {...register('recep_type')} data-testid="customer-business-recep-type-select">
                <option value="01">Contribuyente</option>
                <option value="02">Consumidor Final</option>
                <option value="03">Gobierno</option>
                <option value="04">Extranjero</option>
              </Select>
              <Select label="País de Operación" error={errors.countryId?.message} disabled={isLoadingCountries}
                {...register('countryId')} data-testid="customer-business-country-select">
                <option value="">{isLoadingCountries ? 'Cargando...' : 'Seleccione'}</option>
                {countries?.map((c) => <option key={c.Id} value={c.Id}>{c.Name}</option>)}
              </Select>
            </div>

            <div className="mt-3.5">
              <button
                type="button"
                onClick={handleVerifyRuc}
                disabled={isVerifying}
                data-testid="customer-business-verify-ruc-button"
                className={`${GHOST} w-full justify-center disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {isVerifying ? <Spinner size="sm" /> : <ShieldCheckIcon className="h-3.5 w-3.5" />}
                {isVerifying ? 'Verificando...' : 'Verificar RUC con la DGI'}
              </button>
              {verificationMessage && (
                <p
                  data-testid="customer-business-verify-ruc-alert"
                  className={`mt-2 text-[13px] font-medium ${
                    verificationMessage.startsWith('✓') ? 'text-[#0d9488]' : 'text-[#a24030]'
                  }`}
                >
                  {verificationMessage}
                </p>
              )}
            </div>
          </div>

          <div>
            <GroupLabel>Contacto</GroupLabel>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input label="Email" type="email" placeholder="empresa@ejemplo.com" error={errors.email?.message}
                maxLength={100} required {...register('email')} data-testid="customer-business-email-input" />
              <Input label="Teléfono" placeholder="0000-0000" error={errors.phone?.message} maxLength={16}
                {...register('phone')} data-testid="customer-business-phone-input" />
            </div>
          </div>
        </div>

        <div className={step === LAST_STEP ? 'block' : 'hidden'}>
          <div className={`${INSET_PANEL} mb-4 flex items-center gap-3 px-4 py-3`}>
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-500/10 text-[#0d9488]">
              <IdentificationIcon className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <div className="truncate text-[14px] font-bold text-ink-850 dark:text-white">
                {legalName || 'Cliente sin nombre'}
              </div>
              <div className="mt-px font-mono text-[12px] text-secondary-400">
                {ruc || 'sin identificación'}{dv ? ` · DV ${dv}` : ''}
              </div>
            </div>
          </div>
          <AddressListManager addresses={addresses} onChange={setAddresses} />
        </div>
      </div>

      <div className="sticky -bottom-7 -mx-9 -mb-7 mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[#f0f0f0] bg-[#fafafa] px-9 py-[18px] dark:border-secondary-700 dark:bg-secondary-900/95">
        <div className="flex items-center gap-2">
          <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#fada51] shadow-[0_0_6px_rgba(250,218,81,.6)]" />
          <span className={SECTION_NUM}>PASO {step + 1}/{STEPS.length}</span>
        </div>
        <div className="flex items-center gap-2.5">
          <button type="button" onClick={onCancel} className={GHOST} data-testid="customer-business-cancel-button">Cancelar</button>
          {step > 0 && (
            <button type="button" onClick={() => setStep(step - 1)} className={GHOST}>
              <ArrowLeftIcon className="h-3.5 w-3.5" /> Atrás
            </button>
          )}
          {step < LAST_STEP ? (
            <button type="button" onClick={goNext} className={CTA}>Continuar <ArrowRightIcon className="h-3.5 w-3.5" /></button>
          ) : (
            <button type="submit" disabled={isLoading || submitLocked} className={CTA} data-testid="customer-business-submit-button">
              {isLoading ? <Spinner size="sm" /> : <CheckIcon className="h-3.5 w-3.5" strokeWidth={2.5} />}
              {customer ? 'Actualizar' : 'Crear'} Cliente
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
